/**
 * Firebase Configuration and Utilities
 */

import { initializeApp } from 'firebase/app';
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  updateProfile,
  User,
  GithubAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import {
  initializeFirestore,
  persistentLocalCache,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  DocumentData,
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { firebaseConfig } from "../../../firebase/firebase.config"

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firestore with consistent options (matching launcher)
export const db = initializeFirestore(
  app,
  (typeof window !== 'undefined' && (window as any).Cypress)
    ? {}
    : {
      localCache: persistentLocalCache({
        // Optional: configure cache size limit, default is 40MB
        // cacheSizeBytes: 100 * 1024 * 1024, // 100MB
      }),
    }
);

// Export Firebase services
export const auth = getAuth(app);
export const storage = getStorage(app);
export const functions = getFunctions(app);

// GitHub Auth Provider
const githubProvider = new GithubAuthProvider();

// Auth helpers
export function onAuthChange(callback: (user: User | null) => void) {
  return onAuthStateChanged(auth, callback);
}

export async function signIn(email: string, password: string) {
  return signInWithEmailAndPassword(auth, email, password);
}

export async function signUp(email: string, password: string, displayName: string) {
  const credential = await createUserWithEmailAndPassword(auth, email, password);
  await updateProfile(credential.user, { displayName });
  return credential;
}

export async function signInWithGithub() {
  githubProvider.addScope('read:user');
  return signInWithPopup(auth, githubProvider);
}

export async function signOut() {
  return firebaseSignOut(auth);
}

// Firestore helpers
export { collection, doc, getDoc, getDocs, setDoc, updateDoc, query, where, orderBy, limit, onSnapshot, Timestamp };
export type { DocumentData };

// Cloud Functions helpers
export function callFunction<T, R>(name: string) {
  const fn = httpsCallable<T, R>(functions, name);
  return fn;
}

// Type definitions for our app
export interface AppData {
  id: string;
  name: string;
  description: string;
  version: string;
  iconUrl: string;
  downloadUrl: string;
  developerId: string;
  githubUsername: string;
  developerStripeAccountId: string | null;
  published: boolean;
  price: number;
  currency: string;
  permissions: {
    read: string[];
    write: string[];
    net: string[];
    env: string[];
    run: string[];
  };
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface DeveloperData {
  userId: string;
  email: string;
  displayName: string;
  githubUsername: string;
  stripeConnectedAccountId: string | null;
  stripeOnboardingComplete: boolean;
  payoutsEnabled: boolean;
  createdAt: Timestamp;
}

export interface EntitlementData {
  appId: string;
  purchaseId: string;
  purchasedAt: Timestamp;
}

// Re-export shared type from functions
// Note: We use client SDK Timestamp here, but it's compatible with admin SDK Timestamp
export type { InstalledAppDoc as InstalledAppData } from '../../../firebase/functions/src/types';
